import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { KeyRound } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { AccessKeyLookupField } from "@/components/bookings/AccessKeyLookupField";
import { resolveGuestName } from "@/lib/guestDisplay";
import { useGuestById } from "@/lib/useGuestById";
import { DepartureGuestCheckoutDetail } from "@/components/bookings/DepartureGuestCheckoutDetail";
import { formatIsoDateShort } from "@/lib/bookingDates";
import type { BookingDeparture, Pitch } from "@/types/camping";

export type DepartureCheckoutModalVariant = "key-capture" | "guest-checkout";

export interface DepartureCheckoutModalProps {
  open: boolean;
  onClose: () => void;
  pitches: Pitch[];
  /** key-capture: Transponder/Schlüssel scannen; guest-checkout: Rechnungen & Check-out ohne Suche. */
  variant?: DepartureCheckoutModalVariant;
  /** Anzeige im Titel; Lookup ohne Datumsfilter. */
  referenceDate?: string;
  departureTime?: string;
  canEdit: boolean;
  checkoutStrict: boolean;
  userGroups: string[];
  checkOutLoadingId?: string | null;
  /** Beim Öffnen vorausgewählte Buchung (Check-out oder Scan-Treffer). */
  initialBooking?: BookingDeparture | null;
  onCheckOut: (booking: BookingDeparture) => void | Promise<void>;
  onChanged?: () => void;
  onInvoiceDraftOpened?: (invoiceId: string) => void;
}

export function DepartureCheckoutModal({
  open,
  onClose,
  pitches,
  variant = "key-capture",
  referenceDate,
  departureTime = "11:00",
  canEdit,
  checkoutStrict,
  userGroups,
  checkOutLoadingId,
  initialBooking = null,
  onCheckOut,
  onChanged,
  onInvoiceDraftOpened,
}: DepartureCheckoutModalProps) {
  const isKeyCapture = variant === "key-capture";
  const [scanned, setScanned] = useState<BookingDeparture[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!initialBooking) {
      if (!isKeyCapture) return;
      return;
    }
    setScanned([initialBooking]);
    setActiveId(initialBooking.id);
  }, [open, initialBooking, isKeyCapture]);

  const guestName = useCallback(
    (guestId: string, booking?: BookingDeparture | null) => resolveGuestName(guestId, { booking }),
    [],
  );

  const pitchCode = useCallback(
    (pitchId: string | null) =>
      pitchId ? (pitches.find((x) => x.id === pitchId)?.code ?? pitchId.slice(0, 8)) : "Offen",
    [pitches],
  );

  const activeBooking = useMemo(
    () => scanned.find((b) => b.id === activeId) ?? scanned[0] ?? null,
    [activeId, scanned],
  );

  const activeGuest = useGuestById(activeBooking?.guest_id);

  const activePitch = useMemo(
    () =>
      activeBooking?.pitch_id
        ? pitches.find((p) => p.id === activeBooking.pitch_id) ?? null
        : null,
    [activeBooking, pitches],
  );

  function handleMatch(booking: BookingDeparture) {
    setScanned((prev) => {
      if (prev.some((b) => b.id === booking.id)) return prev;
      return [...prev, booking];
    });
    setActiveId(booking.id);
  }

  async function handleCheckOutBooking(booking: BookingDeparture) {
    await onCheckOut(booking);
    setScanned((prev) => prev.filter((b) => b.id !== booking.id));
    setActiveId((id) => (id === booking.id ? null : id));
    onChanged?.();
  }

  function handleClose() {
    setScanned([]);
    setActiveId(null);
    onClose();
  }

  const titleDate = referenceDate ? formatIsoDateShort(referenceDate) : "heute";

  const modalTitle = isKeyCapture ? "Schlüssel & Transponder – Abreise" : "Check-out";
  const modalDescription = isKeyCapture
    ? `Zurückgegebene Zugänge erfassen · Abreise ${titleDate} ab ${departureTime} Uhr`
    : `Rechnungen, Zahlungen und Check-out · Abreise ${titleDate}`;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={modalTitle}
      description={modalDescription}
      size="xl"
    >
      <div className="space-y-5">
        {isKeyCapture ? (
          <AccessKeyLookupField
            onMatch={handleMatch}
            className="rounded-xl border border-dashed border-separator bg-fill-tertiary/20 p-4"
          />
        ) : null}

        {scanned.length === 0 ? (
          isKeyCapture ? (
            <p className="flex items-center gap-2 text-sm text-label-secondary">
              <KeyRound className="h-4 w-4 shrink-0" aria-hidden />
              Transponder oder Schlüsselnummer scannen, um den zurückgegebenen Zugang zuzuordnen.
            </p>
          ) : null
        ) : (
          <div className="space-y-4">
            {scanned.length > 1 ? (
              <ul className="flex gap-2 overflow-x-auto pb-1">
                {scanned.map((b) => {
                  const isActive = b.id === (activeBooking?.id ?? null);
                  return (
                    <li key={b.id} className="shrink-0">
                      <button
                        type="button"
                        className={clsx(
                          "min-w-[10rem] rounded-xl border px-3 py-2.5 text-left text-sm transition-colors",
                          isActive
                            ? "border-accent bg-accent-soft ring-1 ring-accent/30"
                            : "border-separator bg-surface hover:bg-fill-tertiary/50",
                          b.is_departure_overdue && !isActive && "border-status-red/40",
                        )}
                        onClick={() => setActiveId(b.id)}
                      >
                        <span className="block font-semibold text-label">
                          {guestName(b.guest_id, b)}
                        </span>
                        <span className="mt-0.5 block text-xs text-label-secondary">
                          {pitchCode(b.pitch_id)} · {b.booking_number}
                        </span>
                        {b.is_departure_overdue ? (
                          <span className="mt-1 inline-block text-[0.65rem] font-semibold text-status-red">
                            Überfällig
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {activeBooking ? (
              <DepartureGuestCheckoutDetail
                key={activeBooking.id}
                booking={activeBooking}
                guest={activeGuest}
                pitch={activePitch}
                canEdit={canEdit}
                checkoutStrict={checkoutStrict}
                userGroups={userGroups}
                checkOutLoading={checkOutLoadingId === activeBooking.id}
                onCheckOut={() => void handleCheckOutBooking(activeBooking)}
                onChanged={onChanged}
                onInvoiceDraftOpened={onInvoiceDraftOpened}
              />
            ) : null}
          </div>
        )}
      </div>
    </Modal>
  );
}
